import React, { useState, useEffect } from 'react';
import {
  Button,
  Snackbar,
  Box,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import { Download, Close } from '@mui/icons-material';

interface InstallPWAProps {
  onInstall?: () => void;
}

const InstallPWA: React.FC<InstallPWAProps> = ({ onInstall }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      if (onInstall) onInstall();
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleClose = () => {
    setShowInstallPrompt(false);
  };

  if (isInstalled || !showInstallPrompt) {
    return null;
  }

  return (
    <Snackbar
      open={showInstallPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 80, sm: 20 } }}
    >
      <Card sx={{ 
        minWidth: 300, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Download sx={{ fontSize: 24 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Cài đặt ứng dụng
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Cài đặt để sử dụng offline và truy cập nhanh hơn
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={handleClose}
              sx={{ color: 'white', minWidth: 'auto', p: 0.5 }}
            >
              <Close />
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleInstallClick}
              fullWidth
              sx={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Cài đặt
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Snackbar>
  );
};

export default InstallPWA; 